import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

interface UseChatHistoryActionsOptions {
  /** Re-reads the session list after a change, so the row shows the server's truth. */
  refetch: () => void | Promise<void>
  /** Tells the person a rename or star failed. Delete reports through the dialog instead. */
  onError: (message: string) => void
  /** A chat was deleted; the page leaves it if it was the open one. */
  onDeleted?: (sessionId: string) => void
}

function serverMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = (err.response?.data as { error?: string } | undefined)?.error
    if (msg) return msg
  }
  return fallback
}

/**
 * Rename, star and delete a chat from the History list (#1634). Network only:
 * the list component takes these as props. The server allows rename and star
 * to the chat's owner alone, and refuses a delete while the chat is shared by
 * link or a dashboard uses it, answering 409 with its reason in `error`.
 */
export function useChatHistoryActions({ refetch, onError, onDeleted }: UseChatHistoryActionsOptions) {
  const handleRenameSession = async (sessionId: string, title: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/agent/session/${sessionId}`, { title })
      await refetch()
    } catch (err) {
      onError(serverMessage(err, 'Could not rename the chat. Try again.'))
    }
  }

  const handleToggleStar = async (sessionId: string, starred: boolean) => {
    try {
      await axios.patch(`${API_BASE_URL}/agent/session/${sessionId}`, { starred })
      await refetch()
    } catch (err) {
      onError(serverMessage(err, 'Could not update the chat. Try again.'))
    }
  }

  /** Resolves to the refusal message for the dialog, or to nothing once the chat is gone. */
  const handleDeleteSession = async (sessionId: string): Promise<string | undefined> => {
    try {
      await axios.delete(`${API_BASE_URL}/agent/session/${sessionId}`)
    } catch (err) {
      return serverMessage(err, 'Could not delete the chat. Try again.')
    }
    onDeleted?.(sessionId)
    await refetch()
    return undefined
  }

  return { handleRenameSession, handleToggleStar, handleDeleteSession }
}
